import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Loader2, Save, Mail } from 'lucide-react';

export const JobConfirmationTemplateSettings = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [tags, setTags] = useState([]);
  const [activeField, setActiveField] = useState('body');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/job-confirmation-template');
        setSubject(res.data.template.subject || '');
        setBody(res.data.template.body || '');
        setEnabled(res.data.template.enabled !== false);
        setTags(res.data.available_tags || []);
      } catch (e) {
        toast.error('Failed to load template');
      } finally {
        setLoading(false);
      }
    })();
  }, [api]);

  const insertTag = (tag) => {
    if (activeField === 'subject') {
      setSubject((prev) => `${prev}${tag}`);
    } else {
      setBody((prev) => `${prev}${tag}`);
    }
  };

  const handleSave = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSaving(true);
    try {
      await api.put('/settings/job-confirmation-template', { subject, body, enabled });
      toast.success('Job confirmation template saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card data-testid="job-confirmation-template-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Job Confirmation Email
            </CardTitle>
            <CardDescription>
              Set the subject and body used when sending job confirmations to customers. Use the tags below to insert job details.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label htmlFor="jc-enabled" className="text-sm">Enabled</Label>
            <Switch id="jc-enabled" checked={enabled} onCheckedChange={setEnabled} data-testid="jc-enabled-switch" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label className="label-overline">Subject</Label>
          <Input
            value={subject}
            onFocus={() => setActiveField('subject')}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Job Confirmation — #{job_number}"
            data-testid="jc-subject-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="label-overline">Body</Label>
          <Textarea
            value={body}
            onFocus={() => setActiveField('body')}
            onChange={(e) => setBody(e.target.value)}
            rows={12}
            className="font-mono text-sm"
            data-testid="jc-body-input"
          />
        </div>

        <div className="space-y-2">
          <Label className="label-overline">Insert a tag (click to add to {activeField})</Label>
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <Badge
                key={t.tag}
                variant="outline"
                className="cursor-pointer hover:bg-[#ffd108] hover:text-black transition-colors"
                onClick={() => insertTag(t.tag)}
                title={t.label}
                data-testid={`jc-tag-${t.tag.replace(/[{}]/g, '')}`}
              >
                {t.tag}
              </Badge>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="jc-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Template
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobConfirmationTemplateSettings;
